import { CarouselButtonNext } from '~/components-v2/carousel-button-next/carousel-button-next'
import { LinkCtaNext } from '~/components-v2/link-cta-next/link-cta-next'
import useEmblaCarousel from 'embla-carousel-react'
import clsx from 'clsx'
import type { CSSProperties, ReactNode, TouchEvent, WheelEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

type CarouselRailLink = {
  external?: boolean
  href: string
  label: string
}

type CarouselRailProps = {
  children: ReactNode[]
  className?: string
  desktopEndSpacerSlides?: number
  desktopSlidesToScroll?: number
  desktopVisibleSlides?: number
  mobileSlidesToScroll?: number
  mobileTransition?: 'slide' | 'fade'
  header?: ReactNode
  slideClassName?: string
  showControls?: boolean
  viewAll?: CarouselRailLink
  viewportControls?: boolean
}

type CarouselRailStyle = CSSProperties & {
  '--carousel-rail-end-spacer-slides'?: number
  '--carousel-rail-visible-slides'?: number
}

export const CarouselRail = ({
  children,
  className,
  desktopEndSpacerSlides = 0,
  desktopSlidesToScroll = 1,
  desktopVisibleSlides = 3,
  mobileSlidesToScroll = 1,
  mobileTransition = 'slide',
  header,
  slideClassName,
  showControls = true,
  viewAll,
  viewportControls = false,
}: CarouselRailProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    active: mobileTransition !== 'fade',
    containScroll: false,
    breakpoints: {
      '(min-width: 1024px)': {
        active: true,
        containScroll: 'trimSnaps',
      },
    },
  })
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const wheelLockRef = useRef(false)
  const [isDesktopCarousel, setIsDesktopCarousel] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScroll, setCanScroll] = useState(false)
  const desktopLastStartIndex = Math.max(
    0,
    children.length - desktopVisibleSlides,
  )
  const isMobileFade = mobileTransition === 'fade' && !isDesktopCarousel

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 1024px)')
    const updateDesktopState = () => {
      setIsDesktopCarousel(desktopQuery.matches)
    }

    updateDesktopState()
    desktopQuery.addEventListener('change', updateDesktopState)

    return () => {
      desktopQuery.removeEventListener('change', updateDesktopState)
    }
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    const updateSelectedIndex = () => {
      const index = emblaApi.selectedScrollSnap()
      setCanScroll(emblaApi.canScrollPrev() || emblaApi.canScrollNext())

      if (isDesktopCarousel && index > desktopLastStartIndex) {
        emblaApi.scrollTo(desktopLastStartIndex)
        setSelectedIndex(desktopLastStartIndex)
        return
      }

      setSelectedIndex(index)
    }

    updateSelectedIndex()
    emblaApi.on('reInit', updateSelectedIndex).on('select', updateSelectedIndex)

    return () => {
      emblaApi
        .off('reInit', updateSelectedIndex)
        .off('select', updateSelectedIndex)
    }
  }, [desktopLastStartIndex, emblaApi, isDesktopCarousel])

  const onPrevButtonClick = useCallback(() => {
    if (isMobileFade) {
      setSelectedIndex((current) => Math.max(current - mobileSlidesToScroll, 0))
      return
    }

    if (!emblaApi) return

    const slidesToScroll = isDesktopCarousel
      ? desktopSlidesToScroll
      : mobileSlidesToScroll

    emblaApi.scrollTo(Math.max(selectedIndex - slidesToScroll, 0))
  }, [
    desktopSlidesToScroll,
    emblaApi,
    isDesktopCarousel,
    isMobileFade,
    mobileSlidesToScroll,
    selectedIndex,
  ])

  const onNextButtonClick = useCallback(() => {
    if (isMobileFade) {
      setSelectedIndex((current) =>
        Math.min(current + mobileSlidesToScroll, children.length - 1),
      )
      return
    }

    if (!emblaApi) return

    const slidesToScroll = isDesktopCarousel
      ? desktopSlidesToScroll
      : mobileSlidesToScroll

    if (isDesktopCarousel) {
      emblaApi.scrollTo(
        Math.min(selectedIndex + slidesToScroll, desktopLastStartIndex),
      )
      return
    }

    emblaApi.scrollTo(
      Math.min(
        selectedIndex + slidesToScroll,
        emblaApi.scrollSnapList().length - 1,
      ),
    )
  }, [
    children.length,
    desktopLastStartIndex,
    desktopSlidesToScroll,
    emblaApi,
    isDesktopCarousel,
    isMobileFade,
    mobileSlidesToScroll,
    selectedIndex,
  ])

  const onFadeTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!isMobileFade) return

      const touch = event.touches[0]
      if (!touch) return

      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      }
    },
    [isMobileFade],
  )

  const onFadeTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!isMobileFade || !touchStartRef.current) return

      const touch = event.changedTouches[0]
      if (!touch) return

      const deltaX = touch.clientX - touchStartRef.current.x
      const deltaY = touch.clientY - touchStartRef.current.y
      touchStartRef.current = null

      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return

      if (deltaX < 0) {
        onNextButtonClick()
        return
      }

      onPrevButtonClick()
    },
    [isMobileFade, onNextButtonClick, onPrevButtonClick],
  )

  const onFadeTouchCancel = useCallback(() => {
    touchStartRef.current = null
  }, [])

  const onCarouselWheel = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      const horizontalDelta = event.deltaX
      const verticalDelta = event.deltaY

      if (
        wheelLockRef.current ||
        Math.abs(horizontalDelta) < 20 ||
        Math.abs(horizontalDelta) < Math.abs(verticalDelta)
      ) {
        return
      }

      event.preventDefault()
      wheelLockRef.current = true
      window.setTimeout(() => {
        wheelLockRef.current = false
      }, 450)

      if (horizontalDelta > 0) {
        onNextButtonClick()
        return
      }

      onPrevButtonClick()
    },
    [onNextButtonClick, onPrevButtonClick],
  )

  const prevBtnDisabled = isMobileFade
    ? selectedIndex <= 0
    : !emblaApi || !emblaApi.canScrollPrev()
  const nextBtnDisabled = isMobileFade
    ? selectedIndex >= children.length - 1
    : !emblaApi ||
      (isDesktopCarousel
        ? selectedIndex >= desktopLastStartIndex
        : !emblaApi.canScrollNext())
  const hasDesktopOverflow = children.length > desktopVisibleSlides
  const shouldShowControls =
    showControls &&
    (isMobileFade
      ? children.length > 1
      : canScroll && (!isDesktopCarousel || hasDesktopOverflow))

  const controls = (
    <div className="carousel-controls-next">
      <CarouselButtonNext
        direction="previous"
        disabled={prevBtnDisabled}
        onClick={onPrevButtonClick}
      />
      <CarouselButtonNext
        direction="next"
        disabled={nextBtnDisabled}
        onClick={onNextButtonClick}
      />
    </div>
  )

  return (
    <div
      className={clsx('carousel-rail', className)}
      style={
        {
          '--carousel-rail-end-spacer-slides': desktopEndSpacerSlides,
          '--carousel-rail-visible-slides': desktopVisibleSlides,
        } as CarouselRailStyle
      }
    >
      {header && (
        <div className="carousel-rail__header">
          <div className="carousel-rail__header-content">{header}</div>
          {viewportControls && shouldShowControls && (
            <div className="carousel-rail__viewport-controls">{controls}</div>
          )}
        </div>
      )}

      <div
        className="carousel-rail__viewport"
        onTouchCancel={onFadeTouchCancel}
        onTouchEnd={onFadeTouchEnd}
        onTouchStart={onFadeTouchStart}
        onWheel={onCarouselWheel}
        ref={emblaRef}
      >
        <div className="carousel-rail__container">
          {children.map((child, index) => (
            <div
              className={clsx('carousel-rail__slide', slideClassName)}
              data-active={index === selectedIndex ? 'true' : undefined}
              key={index}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {!header && viewportControls && shouldShowControls && (
        <div className="carousel-rail__viewport-controls">{controls}</div>
      )}

      <div className="carousel-rail__footer">
        {viewAll && (
          <LinkCtaNext
            className="carousel-rail__view-all"
            external={viewAll.external}
            href={viewAll.href}
          >
            {viewAll.label}
          </LinkCtaNext>
        )}
        {shouldShowControls && (
          <div className="carousel-rail__footer-controls">{controls}</div>
        )}
      </div>
    </div>
  )
}
